import React, {
    useEffect,
    useState,
    useCallback
} from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useAuthentication } from 'gw-digital-auth-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ModalNextProvider, Loader } from '@jutro/components';
// eslint-disable-next-line import/no-extraneous-dependencies
import { GatewayDocumentService } from 'gw-capability-gateway-document';
// eslint-disable-next-line import/no-extraneous-dependencies
import { GatewayEndorsementService } from 'gw-capability-gateway-policychange';
import { useValidation } from 'gw-portals-validation-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { messages } from 'gw-capability-policychange-common-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import styles from './QuotePage.module.scss';
import metadata from './QuotePage.metadata.json5';
// import UnderwritingIssues from 'gw-components-platform-react/UnderwritingIssues/UnderwritingIssues';
import NonBlockingMsgPopup from './NonBlockingMsgPopup';

function QuotePage(props) {
    const {
        wizardData, updateWizardData, underwritingIssues
    } = props;
    const filterUWIssuesInCustomOffering = true;
    const { submissionVM } = wizardData;
    const { isLoggedIn, authHeader } = useAuthentication();
    const [allDrivers, setAllDrivers] = useState();
    const [allVehicles, setAllVehicles] = useState();
    const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
    const BLOCKS_BIND = 'BlocksBind';
    const NON_BLOCKING = 'NonBlocking';
    const [isLoading, setLoading] = useState(false);
    const { onValidate, isComponentValid } = useValidation('QuotePage');
    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };

    useEffect(() => {
        const setDriverNames = () => {
            const drivers = _.get(
                submissionVM.value,
                'lobData.businessAuto.coverables.drivers',
                []
            );
            const driverNames = drivers.map((driver) => driver.person.displayName);
            setAllDrivers(driverNames.join(', '));
        };

        const setVehicleNames = () => {
            const vehicles = _.get(
                submissionVM.value,
                'lobData.businessAuto.coverables.vehicles',
                []
            );
            const vehicleNames = vehicles.map((vehicle) => vehicle.displayName);
            setAllVehicles(vehicleNames.join(', '));
        };

        if (isLoggedIn) {
            setDriverNames();
            setVehicleNames();
        }
    }, [isLoggedIn, submissionVM, updateWizardData]);

    const showModal = async (nonBlockingMessage) => {
        const componentProps = {
            title: messages.nonBlockingMessageTitle,
            actionBtnLabel: messages.modalOk,
            nonBlockingMessage: nonBlockingMessage,
        };
        const results = await ModalNextProvider.showModal(
            <NonBlockingMsgPopup {...componentProps} />
        );
        return results;
    };

    useEffect(() => {
        const nonBlockingMessage = underwritingIssues.filter(
            (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING
            && uwIssue.hasApprovalOrRejection === false
        );
        const hasNonBlockingMessage = nonBlockingMessage.length > 0;

        if (hasNonBlockingMessage) {
            try {
                showModal(nonBlockingMessage);
            } catch (err) {
                console.error(err);
            }
        }
    }, []);


    const generateQuoteDocument = useCallback(
        () => {
            ModalNextProvider.showConfirm({
                title: messages.baGenerateQuoteDocument,
                message: messages.baGenerateQuoteDocumentConf,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setLoading(true);
                GatewayEndorsementService.generateQuoteDocumentPortal(
                    submissionVM.value, false, authHeader
                ).then((response) => {
                    setLoading(false);
                    if (response.publicID && response.sessionID) {
                        const downloadUrl = DocumentPageService.downloadDocument(
                            response.publicID,
                            response.sessionID
                        );
                        window.open(downloadUrl, '_blank');
                    } else {
                        ModalNextProvider.showAlert({
                            title: 'Unable to generate quote document',
                            message: 'Problem occured while generating quote document. Please try again later',
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: 'Ok'
                        }).catch(_.noop);
                    }
                }).catch((err) => {
                    console.log(err);
                    setLoading(false);
                });
            }, _.noop);
        },
        [submissionVM.value, authHeader, DocumentPageService]
    );

    const findBlockingPoint = (type) => {
        return underwritingIssues.some((offeringIssue) => {
            if (type === BLOCKS_BIND) {
                return offeringIssue.currentBlockingPoint === BLOCKS_BIND;
            }
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint);
        });
    };

    const hasQuoteOrIssueBlockingUWIssues = () => {
        return submissionVM.status.value === 'Draft' || underwritingIssues.some((offeringIssue) => {
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint)
            || _.includes(BLOCKS_BIND, offeringIssue.currentBlockingPoint);
        });
    };

    const changeNoteForUW = useCallback((value) => {
        _.set(submissionVM.value, 'baseData.noteForUW', value);
        updateWizardData(wizardData);
    }, [submissionVM.value, updateWizardData, wizardData]);

    const handleNext = useCallback(async () => {
        if (submissionVM.baseData.noteForUW.value !== undefined) {
            await GatewayEndorsementService.createNoteForUW(
                submissionVM.value.jobID, submissionVM.baseData.noteForUW.value, authHeader
            );
        }
        return wizardData;
    }, [submissionVM, authHeader, wizardData]);

    const visibilityConditionForNoteToUW = () => {
        if (underwritingIssues.length === 0
                || (underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === BLOCKS_BIND
                        || uwIssue.currentBlockingPoint === 'BlocksQuote'
                        || uwIssue.currentBlockingPoint === 'BlocksQuoteRelease'
                ).length === 0
                && ((underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING && uwIssue.hasApprovalOrRejection === false
                ).length > 0) || (underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING && uwIssue.hasApprovalOrRejection === true
                ).length >= 0)))) {
            return true;
        }
        return false;
    };

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showRequired: true
        },
        drivers: {
            value: allDrivers
        },
        vehicles: {
            value: allVehicles
        },
        dateId: {
            labelPosition: undefined
        },
        underWritingIssues: {
            underwritingIssues: underwritingIssues,
            blockQuote: findBlockingPoint(),
            quoteID: submissionVM.jobID.value,
            filterUWIssuesInCustomOffering,
            job: submissionVM.baseData.jobType.value,
            futureRenewalAvailable: submissionVM.futureRenewalAvailable.value
        },
        BACostAndPremiumGrid: {
            visible: !findBlockingPoint(),
        },
        policyEffectiveDate: {
            visible: findBlockingPoint(),
        },
        uwNoteForUnderwriter: {
            visible: visibilityConditionForNoteToUW(),
            value: submissionVM.baseData.noteForUW.value ? submissionVM.baseData.noteForUW.value : '',
            onValueChange: changeNoteForUW
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [overrideProps, submissionVM]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateQuoteDocument: generateQuoteDocument
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <WizardPage
            onNext={handleNext}
            disableNext={!isComponentValid || hasQuoteOrIssueBlockingUWIssues()}
            cancelLabel={(appConfig.persona === 'policyholder' ? messages.cancelAllChanges : messages.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
